import React, { useEffect, useMemo, useState } from 'react'
import { Layout } from '../../components/Layout/Layout'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { exportRowsAsCsv } from '../../utils/csv'
import { T, useContent } from '../../content/ContentProvider'

/**
 * Read-only audit view of AccountApportionment records. Carriers submit these
 * via POST /api/apportionment to allocate revenue shares per transaction.
 */
export function Apportionments() {
  const { t } = useContent()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    api
      .get('/api/apportionment')
      .then((r) => setRows(Array.isArray(r.data) ? r.data : r.data.content || []))
      .catch((err) => setError(err.response?.data?.message || t('apportionments.loadFailed')))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    if (!search.trim()) return rows
    const s = search.toLowerCase()
    return rows.filter(
      (a) =>
        (a.transactionId || '').toLowerCase().includes(s) ||
        (a.agreementId || '').toLowerCase().includes(s) ||
        (a.accountId || '').toLowerCase().includes(s) ||
        (a.accountProviderRics || '').toLowerCase().includes(s) ||
        (a.mobilityServiceProviderRics || '').toLowerCase().includes(s)
    )
  }, [rows, search])

  const fmtAmount = (p) => {
    if (!p || p.amount == null) return '—'
    const scale = p.scale != null ? p.scale : 2
    return (Number(p.amount) / Math.pow(10, scale)).toFixed(scale) + ' ' + (p.currency || 'EUR')
  }

  return (
    <Layout crumb={t('apportionments.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h">{t('apportionments.title')}</h1>
              <div className="page-head__sub">
                <T k="apportionments.sub" />
              </div>
            </div>
            <div className="page-head__actions">
              <button
                className="btn"
                onClick={() =>
                  exportRowsAsCsv(
                    filtered,
                    [
                      { key: 'transactionId', label: t('apportionments.csv.transaction') },
                      { key: 'agreementId', label: t('apportionments.csv.agreement') },
                      { key: 'accountId', label: t('apportionments.csv.account') },
                      { key: 'accountProviderName', label: t('apportionments.csv.accountProvider') },
                      { key: 'accountProviderRics', label: t('apportionments.csv.apRics') },
                      { key: 'mobilityServiceProviderName', label: t('apportionments.csv.msp') },
                      { key: 'mobilityServiceProviderRics', label: t('apportionments.csv.mspRics') },
                      { label: t('apportionments.csv.amount'), get: (a) => fmtAmount(a.apportionedAmount) },
                      { label: t('apportionments.csv.periodStart'), get: (a) => a.startLogPeriod || '' },
                      { label: t('apportionments.csv.periodEnd'), get: (a) => a.endLogPeriod || '' },
                    ],
                    'mas-apportionments'
                  )
                }
              >
                {Icon.download({ s: 12 })} {t('apportionments.exportCsv')}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{ marginBottom: 14 }}
          >
            {error}
          </div>
        )}

        <div className="card">
          <div className="filter-bar">
            <div className="search-input">
              {Icon.search({ s: 12 })}
              <input
                placeholder={t('apportionments.search.placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              {t('apportionments.count', { shown: filtered.length, total: rows.length })}
            </span>
          </div>

          <div className="tbl-wrap">
            <table className="tbl table--cards">
              <thead>
                <tr>
                  <th>{t('apportionments.column.transaction')}</th>
                  <th>{t('apportionments.column.account')}</th>
                  <th>{t('apportionments.column.accountProvider')}</th>
                  <th>{t('apportionments.column.mobilityProvider')}</th>
                  <th>{t('apportionments.column.amount')}</th>
                  <th>{t('apportionments.column.period')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="apportionments.loading" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="apportionments.empty" />
                    </td>
                  </tr>
                ) : (
                  filtered.map((a, i) => (
                    <tr key={a.id || i}>
                      <td className="id mono" data-label={t('apportionments.column.transaction')} style={{ fontSize: 11 }}>{a.transactionId || '—'}</td>
                      <td className="id" data-label={t('apportionments.column.account')}>{a.accountId || '—'}</td>
                      <td className="muted" data-label={t('apportionments.column.accountProvider')}>
                        {a.accountProviderName || '—'}
                        {a.accountProviderRics ? (
                          <span className="muted mono" style={{ fontSize: 11, marginLeft: 6 }}>
                            ({a.accountProviderRics})
                          </span>
                        ) : null}
                      </td>
                      <td className="muted" data-label={t('apportionments.column.mobilityProvider')}>
                        {a.mobilityServiceProviderName || '—'}
                        {a.mobilityServiceProviderRics ? (
                          <span className="muted mono" style={{ fontSize: 11, marginLeft: 6 }}>
                            ({a.mobilityServiceProviderRics})
                          </span>
                        ) : null}
                      </td>
                      <td className="bold mono" data-label={t('apportionments.column.amount')} style={{ fontSize: 12 }}>
                        {fmtAmount(a.apportionedAmount)}
                      </td>
                      <td className="muted mono" data-label={t('apportionments.column.period')} style={{ fontSize: 11 }}>
                        {a.startLogPeriod ? new Date(a.startLogPeriod).toLocaleDateString('en-GB') : '—'}
                        {a.endLogPeriod ? t('apportionments.periodSeparator') + new Date(a.endLogPeriod).toLocaleDateString('en-GB') : ''}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  )
}
